import { Injectable, inject } from '@angular/core';
import {NetworkService} from '../../../../../../projects/common/src/lib/services/network.service';
import {BaseApiService} from '../base-api.service';
import {FirebaseApi} from '../../firebase-api.service';

@Injectable({
  providedIn: 'root',
})
export class AccountApiService extends BaseApiService {

  constructor() {

    super();
  }

  requestEmailAddressChange(newEmail: string) {
    return this.post(`/account/update/email`, {email: newEmail});
  }

  deleteMyAccount(reason: string) {
    return this.delete(`/account`, {reason: reason});
  }

  /** Everything the account holds about its owner, as one document (GDPR arts. 15 and 20). */
  exportMyData(): Promise<any> {
    return this.post(`/account/export`, {});
  }

  /**
   * Password reset for the signed-in account.
   *
   * No email is sent from the client: the server derives the address from the
   * auth token and sends our own branded message.
   */
  sendPasswordReset() {
    return this.post(`/account/password-reset`, {});
  }

  /**
   * "Forgot password" on the sign-in screen, sent as our own email in the
   * account's language. Open by necessity; the server only ever sends its
   * fixed reset message to an existing account, throttles it, and answers
   * the same whether or not the account exists.
   */
  requestPasswordReset(email: string, language: string) {
    return this.post<{sent: boolean}>(`/account/forgot-password`, {email, language});
  }

  /** Send (or resend) the address confirmation email for the signed-in account. */
  sendEmailVerification(language: string) {
    return this.post<{sent: boolean, verified?: boolean, throttled?: boolean}>(`/account/verify-email`, {language});
  }

  /**
   * Tell the server the browser just changed the password or the second
   * factor -- things Firebase does client-side, where the server cannot see
   * them -- so the account holder gets an email. The server checks the claim
   * against the account before sending anything.
   */
  securityNotice(event: 'password_changed' | 'two_factor_enabled' | 'two_factor_disabled') {
    return this.post<{sent: boolean}>(`/account/security-notice`, {event});
  }

  /**
   * Sign out of every other device. Refresh tokens can only be revoked with
   * the Admin SDK, so this cannot happen in the browser.
   */
  /**
   * Sign out of every other device.
   *
   * The device id goes along so the server can spare this one — matching what
   * it does with the refresh tokens, where the caller is not signed out of the
   * device they clicked from.
   */
  revokeSessions(sessionId?: string) {
    return this.post<{revokedAt: string, resumeToken?: string}>(`/account/revoke-sessions`, {}, sessionHeader(sessionId));
  }

  // ── Two-factor recovery ──────────────────────────────────────────
  // Firebase issues no backup codes, so without these a lost phone means a
  // permanently locked account.

  /**
   * Issue a fresh set, replacing any that exist.
   *
   * The plaintext codes come back ONCE — only hashes are stored — so whatever
   * calls this has to show them immediately or they are gone.
   */
  issueRecoveryCodes() {
    return this.post<{codes: string[], remaining: number}>('/account/2fa/recovery-codes', {});
  }

  /** How many are left, for the "running low" warning. */
  getRecoveryCodeCount() {
    return this.get<{remaining: number}>('/account/2fa/recovery-codes');
  }

  /**
   * Take the second factor off an account using a recovery code.
   *
   * Called from the SIGN-IN screen, so there is no token to send — the caller
   * is by definition someone who cannot finish signing in. The server checks
   * the password and spends a code before changing anything.
   */
  recoverTwoFactor(email: string, password: string, code: string) {
    // Plain post(): getHeaders() already tolerates having no token and simply
    // omits the Authorization header, which is exactly right here — the route
    // is deliberately open because the caller cannot sign in.
    return this.post<{recovered: boolean, remainingCodes: number}>(
      '/account/2fa/recover', {email, password, code});
  }

  /**
   * "This wasn't me": undo an email change with the token from the notice
   * sent to the old address. Open, like recovery: the person may not be able
   * to sign in any more.
   */
  revertEmailChange(token: string) {
    return this.post<{restored: boolean, email: string, passwordReset: boolean}>('/account/email/revert', {token});
  }

  /** The /unsubscribe page: whose email the link came in, before asking. No session needed. */
  outreachUnsubscribeInfo(token: string) {
    return this.get<{found: boolean, name?: string, done?: boolean}>(`/growth/unsubscribe/${encodeURIComponent(token)}`);
  }

  /** Takes that business off our outreach list for good. */
  outreachUnsubscribe(token: string) {
    return this.post<{found: boolean, name?: string, done?: boolean}>(`/growth/unsubscribe/${encodeURIComponent(token)}`, {});
  }

  // ── Devices ──────────────────────────────────────────────────────
  // Firebase Auth keeps no session list, so the app keeps its own. Identifying
  // details are derived server-side from the request; nothing here reports its
  // own device or location, because a client could lie about both.

  /** Say this device is still here; learn whether it has been signed out. */
  sessionHeartbeat(sessionId: string, timeZone?: string) {
    return this.post<{revoked: boolean}>(
      `/account/sessions/heartbeat`, {sessionId, timeZone}, sessionHeader(sessionId));
  }

  /** This device is signing out: take it off the account's device list. */
  endSession(sessionId: string) {
    return this.post<{ended: boolean}>(`/account/sessions/end`, {sessionId}, sessionHeader(sessionId));
  }

  /** Every device signed in to this account. */
  listSessions(sessionId?: string) {
    return this.get<{sessions: any[], onlineWindowMs: number}>(
      `/account/sessions`, sessionHeader(sessionId));
  }

  /** Sign one device out. Refused for the device making the request. */
  revokeSession(id: string, sessionId?: string) {
    return this.delete<{revoked: boolean, id: string}>(
      `/account/sessions/${encodeURIComponent(id)}`, null, sessionHeader(sessionId));
  }
}

/**
 * The device id travels as a header rather than in the body, so it rides along
 * with GET and DELETE too — neither of which has a body to put it in.
 */
function sessionHeader(sessionId?: string) {
  return sessionId ? {'X-Session-Id': sessionId} : {};
}
